/**
 * Helper puro para derivar status visual de comprovação de pagamento.
 * Recebe valores já calculados pela view vw_net_comprovacao_boletim.
 * NÃO duplica lógica de cálculo — apenas converte em label/cor para UI.
 */

export type StatusComprovacao =
  | 'totalmente_comprovado'
  | 'parcial_no_prazo'
  | 'parcial_em_atraso'
  | 'aguardando_no_prazo'
  | 'aguardando_proximo_vencer'
  | 'aguardando_atraso_leve'
  | 'aguardando_atraso_critico'
  | 'sem_valor';

export interface StatusVisual {
  status: StatusComprovacao;
  label: string;
  /** Tailwind classes usando tokens semânticos do design system */
  className: string;
  icon: 'check' | 'clock' | 'alert' | 'x';
}

/**
 * Calcula status combinado cobertura + prazo.
 * @param coberturaPct 0-100 (vem da view)
 * @param diasAtraso negativo = dentro do prazo, positivo = atrasado, null = boletim sem aprovação
 */
export function calcularStatusComprovacao(
  coberturaPct: number | null,
  diasAtraso: number | null,
): StatusVisual {
  const cob = Number(coberturaPct ?? 0);

  // Regra de ouro: 100% = verde sempre, ignora prazo
  if (cob >= 99.9) {
    return {
      status: 'totalmente_comprovado',
      label: 'Totalmente comprovado',
      className: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
      icon: 'check',
    };
  }

  // diasAtraso: negativo = falta tanto pra vencer; positivo = atrasado
  const diasRestantes = diasAtraso === null ? null : -diasAtraso;

  // Parcial
  if (cob > 0) {
    if (diasRestantes === null || diasRestantes >= 0) {
      return {
        status: 'parcial_no_prazo',
        label: `Parcial — ${cob.toFixed(0)}% comprovado`,
        className: 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400',
        icon: 'clock',
      };
    }
    return {
      status: 'parcial_em_atraso',
      label: `Parcial em atraso (${Math.abs(diasRestantes)}d) — ${cob.toFixed(0)}%`,
      className: 'border-orange-500/50 bg-orange-500/10 text-orange-700 dark:text-orange-400',
      icon: 'alert',
    };
  }

  // 0% comprovado
  if (diasRestantes === null) {
    return {
      status: 'aguardando_no_prazo',
      label: 'Aguardando comprovação',
      className: 'border-muted-foreground/30 bg-muted/40 text-muted-foreground',
      icon: 'clock',
    };
  }

  if (diasRestantes >= 7) {
    return {
      status: 'aguardando_no_prazo',
      label: 'Aguardando comprovação',
      className: 'border-muted-foreground/30 bg-muted/40 text-muted-foreground',
      icon: 'clock',
    };
  }
  if (diasRestantes >= 0) {
    return {
      status: 'aguardando_proximo_vencer',
      label: `Vence em ${diasRestantes}d`,
      className: 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400',
      icon: 'clock',
    };
  }
  if (diasRestantes >= -7) {
    return {
      status: 'aguardando_atraso_leve',
      label: `Em atraso (${Math.abs(diasRestantes)}d)`,
      className: 'border-orange-500/50 bg-orange-500/10 text-orange-700 dark:text-orange-400',
      icon: 'alert',
    };
  }
  return {
    status: 'aguardando_atraso_critico',
    label: `Atraso crítico (${Math.abs(diasRestantes)}d)`,
    className: 'border-destructive/50 bg-destructive/10 text-destructive',
    icon: 'x',
  };
}
